import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const cardBadgeVariants = cva('card-badge', {
	variants: {
		tone: {
			green: 'badge-green',
			purple: 'badge-purple',
			blue: 'badge-blue',
			orange: 'badge-orange',
			// Il tono più alto della scala. Senza, «CRITICO» finiva su un tono
			// freddo e sembrava meno grave di «ALLERTA» in arancione — l'opposto
			// di ciò che deve comunicare.
			danger: 'border border-danger/40 bg-danger/15 font-mono text-danger',
			muted: 'border border-border font-mono text-text-muted',
			accent: 'border border-accent/30 bg-accent/10 font-mono text-accent',
			// Ambra su bordo neutro, senza fondo: è la pillola con cui il mock v11
			// annota una testata invece di etichettarla («Giorno di picco: OGGI ·
			// ~58 kW @ 13:00»). `orange` porta anche il fondo tinto e a quella
			// lunghezza diventa una fascia colorata dentro l'intestazione.
			warn: 'border border-border font-mono font-extrabold text-warn',
		},
	},
	defaultVariants: {
		tone: 'muted',
	},
});

export interface CardBadgeProps {
	/** Colore del badge. Default: `muted`. */
	tone?: VariantProps<typeof cardBadgeVariants>['tone'];
	/** Testo del badge. */
	children: React.ReactNode;
}

/** Badge chip per l'intestazione di una {@link PanelCard}. */
const CardBadge = ({ tone, children }: CardBadgeProps) => (
	<span className={cardBadgeVariants({ tone })}>{children}</span>
);
CardBadge.displayName = 'CardBadge';

const CARD_TITLE_ICON_TONE = {
	accent: 'text-accent',
	success: 'text-accent-green',
	purple: 'text-accent-purple',
	warn: 'text-warn',
	danger: 'text-danger',
	muted: 'text-text-muted',
} as const;

export interface PanelCardTitleProps {
	/** Componente icona (es. da lucide-react) mostrato prima del testo. */
	icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
	/** Colore dell'icona. Default: `accent`. */
	tone?: keyof typeof CARD_TITLE_ICON_TONE;
	/** Testo del titolo. */
	children: React.ReactNode;
}

/** Titolo di card con icona colorata iniziale, da passare come `title` di {@link PanelCard}. */
const PanelCardTitle = ({ icon: Icon, tone = 'accent', children }: PanelCardTitleProps) => (
	<span className="card-title inline-flex items-center gap-2">
		<Icon className={cn('h-4 w-4 shrink-0', CARD_TITLE_ICON_TONE[tone])} aria-hidden />
		<span>{children}</span>
	</span>
);
PanelCardTitle.displayName = 'PanelCardTitle';

export interface PanelCardProps {
	/** Titolo dell'intestazione; se assente (e senza `aside`) l'intestazione non viene renderizzata. */
	title?: React.ReactNode;
	/** Lato destro dell'intestazione (badge, azioni). */
	aside?: React.ReactNode;
	/** Corpo della card. */
	children: React.ReactNode;
	/**
	 * La card prende tutta l'altezza del contenitore invece di fermarsi al
	 * contenuto. È il `display:flex;flex-direction:column` che il mock v11 mette
	 * sulle card affiancate: due colonne di una stessa riga devono finire alla
	 * stessa quota, anche quando una porta un grafico alto e l'altra due bassi.
	 */
	fill?: boolean;
	/**
	 * Stacco verso la card successiva, per le card che stanno da sole in pagina.
	 * Dentro una {@link CardGrid} non serve — il margine ce l'ha la griglia
	 * (`.g2`/`.g3`) — ma due card impilate senza griglia si toccherebbero: nel
	 * mock v11 quelle card portano ognuna il proprio `margin-bottom:22px`.
	 */
	spaced?: boolean;
	/**
	 * Titolo e contenuto centrati. È la card-contatore del mock v11
	 * (`text-align:center` sulla card più `justify-content:center`
	 * sull'intestazione): una card che porta un numero solo non ha una colonna di
	 * testo a cui allinearsi, e a sinistra sembra un pannello troncato.
	 */
	centered?: boolean;
}

/** Card standard dell'app con riga di intestazione (token di design `card` / `card-head`). */
const PanelCard = ({ title, aside, children, fill, spaced, centered }: PanelCardProps) => (
	<div
		className={cn(
			'card',
			fill && 'flex h-full flex-col',
			spaced && 'mb-[22px]',
			centered && 'text-center [&>.card-head]:justify-center',
		)}
	>
		{(title || aside) && (
			<div className="card-head">
				{title && <span className="card-title">{title}</span>}
				{aside}
			</div>
		)}
		{children}
	</div>
);
PanelCard.displayName = 'PanelCard';

export { CardBadge, cardBadgeVariants, PanelCard, PanelCardTitle };
